import { ActionIcon, Box, Group, Text, ThemeIcon } from '@mantine/core';
import { IconArrowLeft, IconSparkles, IconX } from '@tabler/icons-react';

import { useAiAssistant } from './AiAssistantProvider';
import { isTreatmentPlanCreateFlow } from './aiAssistantState';
import { GeneralChatFlow } from './GeneralChatFlow';
import { ProgressNoteGenerationFlow } from './ProgressNoteGenerationFlow';
import { TreatmentPlanGuidedFlow } from './TreatmentPlanGuidedFlow';
import { TreatmentPlanRevisionFlow } from './TreatmentPlanRevisionFlow';

export interface AiAssistantDrawerProps {
  mobileOverlay?: boolean;
}

export function AiAssistantDrawer({ mobileOverlay = false }: AiAssistantDrawerProps): JSX.Element {
  const {
    closeDrawer,
    flow,
    revisionContext,
    guidedContext,
    progressNoteContext,
    activeSession,
  } = useAiAssistant();

  const showQuick = flow === 'treatment-plan-quick-generate' && Boolean(guidedContext);
  const showGuided = flow === 'treatment-plan-guided-create' && Boolean(guidedContext);
  const showCreate = showQuick || showGuided;
  const showRevision = flow === 'treatment-plan-revision' && Boolean(revisionContext);
  const showProgressNote = flow === 'progress-note-generate' && Boolean(progressNoteContext);
  const showGeneral = !showCreate && !showRevision && !showProgressNote;

  const title = showQuick
    ? 'Quick treatment plan generate'
    : showGuided
      ? 'Guided treatment planner'
      : showRevision
        ? 'Revise treatment plan'
        : showProgressNote
          ? 'Generate progress note'
          : 'AI assistant';

  const createMountKey = activeSession && isTreatmentPlanCreateFlow(activeSession.flow)
    ? `${activeSession.flow}:${activeSession.launchNonce}`
    : 'create-idle';

  return (
    <Box
      component="aside"
      aria-label="AI assistant"
      h="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        background: 'var(--mantine-color-white)',
        borderLeft: mobileOverlay ? undefined : '1px solid var(--mantine-color-gray-4)',
      }}
    >
      <Group
        justify="space-between"
        align="center"
        px="md"
        py="sm"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-white)',
          flexShrink: 0,
        }}
      >
        <Group gap="xs" wrap="nowrap">
          {mobileOverlay && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={closeDrawer}
              aria-label="Back"
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          )}
          <ThemeIcon size="md" variant="light" color="gold" radius="sm">
            <IconSparkles size={15} />
          </ThemeIcon>
          <Text fw={600} size="md">
            {title}
          </Text>
        </Group>
        <ActionIcon variant="subtle" color="gray" onClick={closeDrawer} aria-label="Close AI assistant">
          <IconX size={18} />
        </ActionIcon>
      </Group>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {guidedContext && isTreatmentPlanCreateFlow(flow) && (
          <Box h="100%" style={{ display: showCreate ? 'block' : 'none' }}>
            <TreatmentPlanGuidedFlow
              key={createMountKey}
              context={guidedContext}
              launchNonce={activeSession?.launchNonce}
            />
          </Box>
        )}
        {revisionContext && (
          <Box
            h="100%"
            style={{
              display: showRevision ? 'flex' : 'none',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <TreatmentPlanRevisionFlow
              key={activeSession?.flow === 'treatment-plan-revision' ? activeSession.launchNonce : 'revision'}
              context={revisionContext}
            />
          </Box>
        )}
        {progressNoteContext && (
          <Box h="100%" style={{ display: showProgressNote ? 'block' : 'none' }}>
            <ProgressNoteGenerationFlow
              key={
                activeSession?.flow === 'progress-note-generate'
                  ? activeSession.launchNonce
                  : 'progress-note'
              }
              context={progressNoteContext}
            />
          </Box>
        )}
        {/* General-purpose chat: fallback for pages without a specialized flow.
            Kept mounted so its conversation survives flow switches. */}
        <Box h="100%" style={{ display: showGeneral ? 'block' : 'none' }}>
          <GeneralChatFlow />
        </Box>
      </Box>
    </Box>
  );
}
